"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import {
  FaFacebook,
  FaWhatsapp,
  FaRegCopy,
  FaBullseye,
  FaChartLine,
  FaCoins,
  FaFilter,
  FaSearch,
  FaMapMarkerAlt,
  FaBuilding,
  FaLayerGroup,
  FaTimes,
} from "react-icons/fa"
import { motion, AnimatePresence } from "framer-motion"
import { CircleLoader, ClipLoader } from "react-spinners"
import { toast } from "react-hot-toast"

const districts = [
  "Ariyalur",
  "Chengalpattu",
  "Chennai",
  "Coimbatore",
  "Cuddalore",
  "Dharmapuri",
  "Dindigul",
  "Erode",
  "Kallakurichi",
  "Kanchipuram",
  "Kanyakumari",
  "Karur",
  "Krishnagiri",
  "Madurai",
  "Nagapattinam",
  "Namakkal",
  "Nilgiris",
  "Perambalur",
  "Pudukkottai",
  "Ramanathapuram",
  "Ranipet",
  "Salem",
  "Sivaganga",
  "Tenkasi",
  "Thanjavur",
  "Theni",
  "Thoothukudi",
  "Tiruchirappalli",
  "Tirunelveli",
  "Tirupathur",
  "Tiruppur",
  "Tiruvallur",
  "Tiruvannamalai",
  "Tiruvarur",
  "Vellore",
  "Viluppuram",
  "Virudhunagar",
]

// Mock data for demonstration
const mockStartups = [
  {
    _id: "1",
    startupDetails: { startupName: "TechFlow Solutions" },
    location: "Chennai",
    stage: "Series A",
    sector: "Tech",
    shortDescription:
      "Revolutionary AI-powered workflow automation platform that helps businesses streamline their operations and increase productivity by up to 300%.",
    longDescription:
      "TechFlow Solutions is at the forefront of business automation, leveraging cutting-edge artificial intelligence to transform how companies operate. Our platform integrates seamlessly with existing systems, providing intelligent workflow automation that adapts to your business needs.",
    logoUrl: "/placeholder.svg?height=200&width=400",
    youtube: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    amountSeeking: 5000000,
    equityOffered: 15,
    valuation: 33333333,
    fundUsage: "Product development and market expansion",
    minimumInvestment: 100000,
    ticketSize: 500000,
    roleProvided: "Advisory role with quarterly updates",
    amountRaised: 1500000,
    status: "approved",
    userId: "user1",
  },
  {
    _id: "2",
    startupDetails: { startupName: "EcoGreen Innovations" },
    location: "Coimbatore",
    stage: "Seed",
    sector: "Environment",
    shortDescription:
      "Sustainable packaging solutions using biodegradable materials that decompose within 90 days, helping businesses reduce their environmental footprint.",
    longDescription:
      "EcoGreen Innovations is pioneering the future of sustainable packaging. Our biodegradable materials are made from agricultural waste, creating a circular economy that benefits both businesses and the environment.",
    logoUrl: "/placeholder.svg?height=200&width=400",
    youtube: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    amountSeeking: 2000000,
    equityOffered: 20,
    valuation: 10000000,
    fundUsage: "Manufacturing setup and R&D",
    minimumInvestment: 50000,
    ticketSize: 200000,
    roleProvided: "Strategic advisor with monthly reports",
    amountRaised: 400000,
    status: "approved",
    userId: "user2",
  }
]

function StartUps() {
  const [selectedStartup, setSelectedStartup] = useState(null)
  const [selectedStartupid, setSelectedStartupid] = useState(null)
  const [selectedFunding, setSelectedFunding] = useState(null);
  const [showForm, setShowForm] = useState(false)
  const [filters, setFilters] = useState({ district: "", sector: "", stage: "", search: "" })
  const [startups, setStartups] = useState([])
  const [loading, setLoading] = useState(true)
  const [investorType, setInvestorType] = useState("Individual")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [newAmountRaised, setNewAmountRaised] = useState(selectedFunding?.amountRaised || 0);
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    fetchFundings()
  }, [])

  const fetchFundings = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/fundings`)
      const approvedFundings = response.data.filter(funding => funding.status === 'approved')

      // Fetch startup details for each funding
      const enrichedFundings = await Promise.all(approvedFundings.map(async (funding) => {
        try {
          const startupRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${funding.userId}`)
          return {
            ...funding,
            startupDetails: startupRes.data
          }
        } catch (err) {
          console.error(`Error fetching startup for ID ${funding.userId}:`, err)
          return {
            ...funding,
            startupDetails: null
          }
        }
      }))

      setStartups(enrichedFundings)
    } catch (error) {
      console.error('Error fetching fundings:', error)
      setStartups(mockStartups)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const errors = {
      name: '',
      email: '',
      phone: ''
    };
    let isValid = true;

    if (!name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
      isValid = false;
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email';
      isValid = false;
    }

    if (!phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      errors.phone = 'Phone must be 10 digits';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmitInterest = async (fundingId) => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get userId from localStorage or set to null if not found
      const userId = localStorage.getItem('startupId') || null;

      await axios.post(`${process.env.REACT_APP_API_URL}/api/interests`, {
        fundingId,
        investorType,
        name,
        email,
        phone,
        userId // Include the userId in the request
      })
      toast.success("Interest submitted successfully!");
      setShowForm(false)
      setSelectedStartup(null)
      setInvestorType("Individual")
      setName("")
      setEmail("")
      setPhone("")
    } catch (error) {
      console.error("Error submitting interest:", error)
      toast.error("Failed to submit interest");
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href)
    toast.success("URL copied to clipboard!");
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }))
  }

  const filteredStartups = startups.filter((startup) => {
    return (
      (filters.district === "" || startup.location === filters.district) &&
      (filters.sector === "" || startup.sector === filters.sector) &&
      (filters.stage === "" || startup.stage === filters.stage) &&
      (filters.search === "" ||
        startup.startupDetails?.startupName.toLowerCase().includes(filters.search.toLowerCase()) ||
        startup.shortDescription.toLowerCase().includes(filters.search.toLowerCase()))
    )
  })

  const convertToEmbedUrl = (url) => {
    if (url.includes('youtu.be')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    if (url.includes('youtube.com/embed')) {
      return url;
    }

    return url;
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30">
      {/* Hero Section with Enhanced Mobile Responsiveness */}
      <section className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 via-purple-600/90 to-indigo-700/90">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fillRule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fillOpacity=\'0.05\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-10 sm:py-14 md:py-10 lg:py-20 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Text Section */}
            <motion.div
              className="lg:col-span-7 text-white text-center lg:text-left"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-3xl font-bold leading-snug mb-4 sm:mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                Invest in Start-Ups
                <span className="block text-blue-200 mt-1 sm:mt-2">with TACTOS Locally</span>
              </h1>

              <p className="text-sm sm:text-base text-blue-100/90 max-w-2xl mb-5 sm:mb-7 leading-relaxed font-light mx-auto lg:mx-0">
                At TACTOS Strategic Solutions, we connect local investors with high-potential startups from your own
                region—startups that are ready to scale, innovate, and create lasting impact.
              </p>

              <p className="text-sm sm:text-base text-blue-100/80 max-w-2xl mb-6 sm:mb-8 leading-relaxed mx-auto lg:mx-0">
                By investing locally, you're not just growing your portfolio—you're fueling the next generation of
                entrepreneurs and helping shape the economic future of your community.
              </p>
            </motion.div>

            {/* Right Cards Section */}
            <motion.div
              className="lg:col-span-5 grid gap-4 sm:gap-6 mt-8 lg:mt-0"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2 }}
            >
              {[
                {
                  icon: <FaBullseye className="text-lg sm:text-xl text-blue-300" />,
                  title: "High Growth Potential",
                  color: "from-blue-500/20 to-cyan-500/20",
                },
                {
                  icon: <FaChartLine className="text-lg sm:text-xl text-emerald-300" />,
                  title: "Portfolio Diversification",
                  color: "from-emerald-500/20 to-teal-500/20",
                },
                {
                  icon: <FaCoins className="text-lg sm:text-xl text-amber-300" />,
                  title: "Wealth Creation",
                  color: "from-amber-500/20 to-orange-500/20",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  className={`bg-gradient-to-r ${item.color} backdrop-blur-sm border border-white/20 rounded-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 hover:shadow-2xl transition-all duration-300`}
                >
                  <div className="p-2 rounded-xl bg-white/10 backdrop-blur-sm flex-shrink-0">
                    {item.icon}
                  </div>
                  <h2 className="text-sm sm:text-sm font-medium text-white">{item.title}</h2>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content - Enhanced Mobile Layout */}
      <div className="container px-4 py-6 sm:py-8 lg:py-12 sm:px-6 lg:px-0">
        <div className="flex gap-4 lg:gap-8">
          {/* Left Sidebar - Fixed Position */}
          <div className="hidden lg:block w-1/3 xl:w-1/4 bg-transparent border border-gray-200/50 rounded-3xl shadow-xl p-6 lg:sticky lg:top-28 h-fit overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent flex items-center gap-3">
                <FaFilter className="text-blue-500 text-lg" />
                Filters
              </h2>
              <button
                onClick={() => setFilters({ district: "", sector: "", stage: "", search: "" })}
                className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 rounded-xl font-semibold text-gray-700 transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm"
              >
                Clear All
              </button>
            </div>

            <div className="space-y-6">
              {/* Search - Full Width */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FaSearch className="text-blue-500 text-sm" />
                  Search Startups
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={filters.search}
                    onChange={handleSearchChange}
                    className="w-full px-4 py-3 pl-12 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg text-sm"
                  />
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                </div>
              </div>

              {/* District Filter - Full Width */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-red-500 text-sm" />
                  District
                </label>
                <select
                  name="district"
                  value={filters.district}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg appearance-none cursor-pointer text-sm"
                >
                  <option value="">All Districts</option>
                  {districts.map((district, idx) => (
                    <option key={idx} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sector Filter - Full Width */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FaBuilding className="text-green-500 text-sm" />
                  Sector
                </label>
                <select
                  name="sector"
                  value={filters.sector}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg appearance-none cursor-pointer text-sm"
                >
                  <option value="">All Sectors</option>
                  <option value="IT">IT</option>
                  <option value="Tech">Technology</option>
                  <option value="Environment">Environment</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                </select>
              </div>

              {/* Stage Filter - Full Width */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FaLayerGroup className="text-purple-500 text-sm" />
                  Stage
                </label>
                <select
                  name="stage"
                  value={filters.stage}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg appearance-none cursor-pointer text-sm"
                >
                  <option value="">All Stages</option>
                  <option value="Ideation">Ideation</option>
                  <option value="Seed">Seed</option>
                  <option value="Series A">Series A</option>
                  <option value="Series B">Series B</option>
                  <option value="Growth">Growth</option>
                </select>
              </div>
            </div>
          </div>

          {/* Right Content - Scrollable Section */}
          <div className="flex-1 overflow-y-auto">
            <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Investment Opportunities
                </h2>
              </div>

              <span className="text-sm sm:text-lg font-normal text-gray-500 bg-white/50 backdrop-blur-sm px-3 sm:px-4 py-1 sm:py-2 rounded-full border border-gray-200/50 w-fit">
                {filteredStartups.length} startups
              </span>
            </div>

            {/* Loading Spinner */}
            {loading && (
              <div className="flex items-center justify-center h-screen">
                <CircleLoader color="#2563eb" size={48} />
                <span className="ml-4 text-black-600 text-lg font-semibold">Loading Details...</span>
              </div>
            )}

            {/* Startup Cards Grid - Enhanced Mobile */}
            {!loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                {filteredStartups.length > 0 ? (
                  filteredStartups.map((startup, idx) => (
                    <motion.div
                      key={startup._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: idx * 0.1 }}
                      whileHover={{ y: -4, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      className="group cursor-pointer"
                      onClick={() => setSelectedStartup(startup)}
                    >
                      <div className="bg-white/70 border border-white/20 rounded-2xl sm:rounded-3xl transition-all duration-500 overflow-hidden group-hover:border-blue-200/50">
                        {/* Image Section */}
                        <div className="relative overflow-hidden">
                          <img
                            src={startup.logoUrl || "/placeholder.svg?height=200&width=400"}
                            alt="Startup Logo"
                            className="w-full h-36 sm:h-40 object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
                            <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-white/90 backdrop-blur-sm rounded-full text-[10px] sm:text-xs font-semibold text-gray-700 border border-white/50">
                              {startup.stage}
                            </span>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>

                        {/* Content Section */}
                        <div className="p-1 sm:p-2 space-y-1 sm:space-y-2">
                          <h3 className="text-lg sm:text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                            {startup.startupDetails?.startupName || "Unknown Startup"}
                          </h3>

                          <div className="flex flex-row items-center gap-2 text-[10px] sm:text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <FaMapMarkerAlt className="text-red-400 text-xs" />
                              {startup.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaBuilding className="text-green-400 text-xs" />
                              {startup.sector}
                            </span>
                          </div>

                          <p className="text-gray-600 text-[10px] sm:text-xs leading-snug line-clamp-3">
                            {startup.shortDescription.length > 100
                              ? `${startup.shortDescription.slice(0, 25)}...`
                              : startup.shortDescription}
                          </p>

                          {/* Stats Section */}
                          <div className="grid grid-cols-3 gap-1 sm:gap-2 pt-2 border-t border-gray-100">
                            <div className="text-center">
                              <p className="text-[9px] sm:text-[10px] text-gray-500 mb-0.5">Seeking</p>
                              <p className="font-bold text-green-600 text-xs sm:text-sm">
                                ₹{(startup.amountSeeking / 100000).toFixed(1)}L
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-[9px] sm:text-[10px] text-gray-500 mb-0.5">Equity</p>
                              <p className="font-bold text-blue-600 text-xs sm:text-sm">
                                {startup.equityOffered}%
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-[9px] sm:text-[10px] text-gray-500 mb-0.5">Min Invest</p>
                              <p className="font-bold text-purple-600 text-xs sm:text-sm">
                                ₹{(startup.minimumInvestment / 100000).toFixed(1)}L
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  !loading && (
                    <div className="col-span-full text-center py-12 sm:py-16">
                      <div className="bg-white/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-8 sm:p-12 border border-gray-200/50">
                        <FaSearch className="mx-auto text-4xl sm:text-6xl text-gray-300 mb-4 sm:mb-6" />
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-600 mb-2 sm:mb-4">No startups found</h3>
                        <p className="text-gray-500 text-base sm:text-lg">
                          Try adjusting your filters to see more results.
                        </p>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Startup Detail Modal - Enhanced Mobile */}
      <AnimatePresence>
        {selectedStartup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setSelectedStartup(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white/90 backdrop-blur-xl w-full max-w-7xl rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] overflow-y-auto border border-white/20"
              onClick={(e) => e.stopPropagation()}
              style={{
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none' 
              }}
            >
              <div className="relative p-4 sm:p-6 lg:p-8">
                <button
                  onClick={() => setSelectedStartup(null)}
                  className="absolute top-3 sm:top-4 lg:top-6 right-3 sm:right-4 lg:right-6 z-10 p-2 sm:p-3 bg-white/80 backdrop-blur-sm hover:bg-white rounded-xl sm:rounded-2xl transition-all duration-300 hover:scale-110 shadow-lg"
                >
                  <FaTimes className="text-gray-600 text-lg sm:text-xl" />
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-6 sm:gap-8 lg:gap-8 lg:pr-8 lg:pb-12">
                  {/* Left Section: Video + Description - Mobile Optimized */}
                  <div className="space-y-8 sm:space-y-8">
                    {selectedStartup.youtube && (
                      <div className="aspect-video rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border border-gray-200/50 h-[50%]">
                        <iframe
                          src={convertToEmbedUrl(selectedStartup.youtube)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="Startup Video"
                        />
                      </div>
                    )}

                    <div className="bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200/50 lg:h-200">
                      <h3 className="text-xl sm:text-1xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                        <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
                        About the Startup
                      </h3>
                      <div className="max-h-16 sm:max-h-60 overflow-y-auto pr-2">
                        <p className="text-gray-700 leading-relaxed text-sm sm:text-base lg:text-lg">
                          {selectedStartup.longDescription}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right Section: Company Info + Details - Mobile Optimized */}
                  <div className="space-y-4 sm:space-y-8">
                    {/* Company Header - Mobile Optimized */}
                    <div className="text-center space-y-1 sm:space-y-6 bg-gradient-to-br from-blue-50/50 to-purple-50/50 backdrop-blur-sm rounded-xl sm:rounded-2xl py-1 sm:p-4 border border-gray-200/50">
                      <img
                        src={selectedStartup.logoUrl || "/placeholder.svg"}
                        alt="Logo"
                        className="w-16 sm:w-20 lg:w-24 h-16 sm:h-20 lg:h-24 object-cover rounded-xl sm:rounded-2xl border-4 border-white shadow-xl mx-auto"
                      />
                      <div>
                        <h2 className="text-2xl sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                          {selectedStartup.startupDetails?.startupName}
                        </h2>
                        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mt-3 sm:mt-4 text-xs sm:text-sm">
                          <span
                            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 bg-white/70 backdrop-blur-sm rounded-full border border-gray-200/50"
                          >
                            <FaMapMarkerAlt className="text-red-400 text-xs" />
                            {selectedStartup.location}
                          </span>
                          <span className="px-2 sm:px-3 py-1 bg-blue-100/70 backdrop-blur-sm text-blue-700 rounded-full border border-blue-200/50 font-semibold">
                            {selectedStartup.stage}
                          </span>
                          <span className="px-2 sm:px-3 py-1 bg-green-100/70 backdrop-blur-sm text-green-700 rounded-full border border-green-200/50 font-semibold">
                            {selectedStartup.sector}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-white/70 to-gray-50/70 backdrop-blur-sm rounded-xl px-4 py-1 border border-gray-200/50 shadow-xl">
                      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-1">
                        <div className="w-1 h-5 bg-gradient-to-b from-green-500 to-blue-600 rounded-full"></div>
                        Funding Details
                      </h4>
                      <div className="grid grid-cols-4 text-xs">
                        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-1">
                          <p className="text-gray-500 font-medium">Amount Seeking</p>
                          <p className="font-bold text-green-600 text-base">
                            ₹{(selectedStartup.amountSeeking / 100000).toFixed(1)}L
                          </p>
                        </div>
                        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-1">
                          <p className="text-gray-500 font-medium">Equity Offered</p>
                          <p className="font-bold text-blue-600 text-base">
                            {selectedStartup.equityOffered}%
                          </p>
                        </div>
                        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-1">
                          <p className="text-gray-500 font-medium">Valuation</p>
                          <p className="font-bold text-purple-600 text-sm">
                            ₹{(selectedStartup.valuation / 10000000).toFixed(1)}Cr
                          </p>
                        </div>
                        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-1">
                          <p className="text-gray-500 font-medium">Min Investment</p>
                          <p className="font-bold text-indigo-600 text-sm">
                            ₹{(selectedStartup.minimumInvestment / 100000).toFixed(1)}L
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar - Mobile Optimized (Smaller) */}
                      <div className="bg-white/50 backdrop-blur-sm rounded-lg p-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-600 font-medium">Amount Raised</span>
                          <span className="font-bold text-green-600 text-sm">
                            ₹{(selectedStartup.amountRaised / 100000).toFixed(1)}L
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{
                              width: `${Math.min(
                                ((selectedStartup.amountRaised || 0) / (selectedStartup.amountSeeking || 1)) * 100,
                                100
                              )}%`,
                            }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                            className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full shadow-lg"
                          />
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-gray-500">
                          <p>₹{newAmountRaised?.toLocaleString() || 0} of ₹{selectedStartup.amountSeeking} raised</p>
                          <p>
                            {Math.min(
                              ((selectedStartup.amountRaised || 0) / (selectedStartup.amountSeeking || 1)) * 100,
                              100
                            ).toFixed(1)}%
                            funded
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions - Mobile Optimized (Smaller) */}
                    <div className="flex flex-col items-center space-y-3">
                      {/* Share buttons */}
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          onClick={() =>
                            window.open(`https://wa.me/?text=${encodeURIComponent(window.location.href)}`, "_blank")
                          }
                          className="flex items-center gap-1 px-2 py-1 bg-green-500/10 backdrop-blur-sm border border-green-200/50 rounded-xl text-green-600 font-semibold hover:bg-green-500/20 transition-all duration-200 text-xs"
                        >
                          <FaWhatsapp className="text-xs" />
                          WhatsApp
                        </button>
                        <button
                          onClick={() =>
                            window.open(
                              `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                                window.location.href
                              )}`,
                              "_blank"
                            )
                          }
                          className="flex items-center gap-1 px-2 py-1 bg-blue-500/10 backdrop-blur-sm border border-blue-200/50 rounded-xl text-blue-600 font-semibold hover:bg-blue-500/20 transition-all duration-200 text-xs"
                        >
                          <FaFacebook className="text-xs" />
                          Facebook
                        </button>
                        <button
                          onClick={handleCopyUrl}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-500/10 backdrop-blur-sm border border-gray-200/50 rounded-xl text-gray-600 font-semibold hover:bg-gray-500/20 transition-all duration-200 text-xs"
                        >
                          <FaRegCopy className="text-xs" />
                          Copy
                        </button>
                      </div>

                      {/* Express Interest button */}
                      <button
                        onClick={() => setShowForm(true)}
                        className="w-fit px-4 py-2 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white font-bold rounded-xl hover:from-blue-700 hover:via-purple-700 hover:to-indigo-800 transition-all duration-200 text-sm"
                      >
                        Express Interest to Invest
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interest Form Modal - Enhanced Mobile */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white/95 backdrop-blur-xl w-full max-w-md rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 relative z-[71]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl sm:text-2xl font-bold text-center bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-6 sm:mb-8">
                Submit Your Interest
              </h3>

              <form
                className="space-y-4 sm:space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmitInterest(selectedStartup._id);
                }}
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Investor Type</label>
                  <select
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 appearance-none cursor-pointer text-sm sm:text-base"
                    value={investorType}
                    onChange={(e) => setInvestorType(e.target.value)}
                  >
                    <option value="Individual">Individual</option>
                    <option value="Institution">Institution</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Name *</label>
                  <input
                    type="text"
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 backdrop-blur-sm border ${formErrors.name ? 'border-red-500' : 'border-gray-200/50'
                      } rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 text-sm sm:text-base`}
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                      setName(value);
                      if (formErrors.name) {
                        setFormErrors({ ...formErrors, name: '' });
                      }
                    }}
                    required
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Email *</label>
                  <input
                    type="email"
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 backdrop-blur-sm border ${formErrors.email ? 'border-red-500' : 'border-gray-200/50'
                      } rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 text-sm sm:text-base`}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (formErrors.email) {
                        setFormErrors({ ...formErrors, email: '' });
                      }
                    }}
                    required
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Phone *</label>
                  <input
                    type="tel"
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 backdrop-blur-sm border ${formErrors.phone ? 'border-red-500' : 'border-gray-200/50'
                      } rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 text-sm sm:text-base`}
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      setPhone(value);
                      if (formErrors.phone) {
                        setFormErrors({ ...formErrors, phone: '' });
                      }
                    }}
                    maxLength={10}
                    required
                  />
                  {formErrors.phone && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.phone}</p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl sm:rounded-2xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 hover:scale-105 hover:shadow-xl text-sm sm:text-base flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <ClipLoader color="#ffffff" size={18} className="mr-2" />
                    ) : null}
                    {isSubmitting ? 'Submitting...' : 'Submit Interest'}
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-white/50 backdrop-blur-sm border border-gray-200/50 text-gray-700 font-semibold rounded-xl sm:rounded-2xl hover:bg-gray-100/50 transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                    onClick={() => setShowForm(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay for mobile sidebar - Simplified */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

export default StartUps;